import { STAGES, type StageChoice, parseCheque, parseHq, parseStages } from '@nemo/core/model';

/** A pasted cell either becomes a value or says, in one line, why it cannot. */
export type Parsed<T> = { ok: true; value: T } | { ok: false; reason: string };

function ok<T>(value: T): Parsed<T> {
  return { ok: true, value };
}

function no<T>(reason: string): Parsed<T> {
  return { ok: false, reason };
}

export function parseTierText(text: string): Parsed<number | null> {
  const trimmed = text.trim();
  if (trimmed === '') return ok(null);
  const value = Number(trimmed);
  if (!Number.isInteger(value) || value < 0 || value > 5) {
    return no(`"${text}" is not a tier: use a whole number from 0 to 5, or nothing`);
  }
  return ok(value);
}

/**
 * What the Stages cell copies, read back: "Seed (leads), Series A" is leads at
 * seed and invests at Series A. Every stage is answered, so a stage the text
 * leaves out is a no to both.
 */
export function parseStageChoicesText(text: string): Parsed<StageChoice[]> {
  const leading = new Set<string>();
  const tokens = text.split(/[,/]/).map((token) => {
    const bare = token.replace(/\(\s*leads?\s*\)/i, '').trim();
    if (bare !== token.trim()) leading.add(bare.toLowerCase());
    return bare;
  });
  const parsed = parseStages(tokens.join(','));
  if (parsed.unrecognised.length > 0) {
    return no(`"${parsed.unrecognised.join(', ')}" is not a stage`);
  }
  const leads = parseStages([...leading].join(',')).stages;
  return ok(
    STAGES.map((stage) => ({
      stage,
      participates: parsed.stages.includes(stage),
      leads: leads.includes(stage),
    })),
  );
}

export function parseCountriesText(text: string): Parsed<string[]> {
  const codes: string[] = [];
  const unreadable: string[] = [];

  for (const part of text.split(/[,/&]/)) {
    const token = part.trim();
    if (token === '') continue;
    if (/^[A-Za-z]{2}$/.test(token)) {
      codes.push(token.toUpperCase());
      continue;
    }
    const parsed = parseHq(token);
    if (parsed.countries.length === 0) unreadable.push(token);
    else codes.push(...parsed.countries);
  }

  if (unreadable.length > 0) return no(`"${unreadable.join(', ')}" is not a country`);
  return ok([...new Set(codes)]);
}

export function parseListText(text: string): Parsed<string[]> {
  return ok(
    text
      .split(',')
      .map((part) => part.trim())
      .filter((part) => part !== ''),
  );
}

export interface ChequeValue {
  min: number | null;
  max: number | null;
  currency: string | null;
}

export function parseChequeText(text: string): Parsed<ChequeValue> {
  const trimmed = text.trim();
  if (trimmed === '') return ok({ min: null, max: null, currency: null });
  const range = parseCheque(trimmed);
  if (range === null) return no(`"${text}" is not a cheque range: try "$1M–$50M"`);
  return ok({ min: range.min, max: range.max, currency: range.currency });
}

export function parseTextValue(text: string): Parsed<string | null> {
  const trimmed = text.trim();
  return ok(trimmed === '' ? null : trimmed);
}
